import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient, type InvalidateQueryFilters } from '@tanstack/react-query';

/** Payload of `event: change` on /api/v1/events (no secrets). */
export interface ChangeEvent {
  type: 'job' | 'target' | 'vps' | 'project' | 'config' | 'backup' | 'migration' | 'notification';
  id: string;
  projectKey?: string;
  vpsId?: string;
  targetId?: string;
}

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

/** Which cached queries a change event makes stale. */
export function invalidationsFor(e: ChangeEvent): InvalidateQueryFilters[] {
  // The overview and the cross-project deployment index both reflect every kind of change.
  const out: InvalidateQueryFilters[] = [{ queryKey: ['overview'] }, { queryKey: ['deployments'] }];
  const project = (key?: string) => {
    if (!key) return;
    out.push({ queryKey: ['project', key], exact: true }, { queryKey: ['project', key, 'targets'] }, { queryKey: ['project', key, 'jobs'] }, { queryKey: ['project', key, 'environments'] });
  };
  switch (e.type) {
    case 'job':
      out.push({ queryKey: ['jobs'] }, { queryKey: ['job', e.id] }, { queryKey: ['projects'] });
      project(e.projectKey);
      if (e.projectKey) out.push({ queryKey: ['project', e.projectKey, 'releases'] });
      if (e.targetId) out.push({ queryKey: ['target', e.targetId] });
      if (e.vpsId) out.push({ queryKey: ['vps'] });
      break;
    case 'target':
      // Shortcuts are a view over targets: a removal or a route change flips their state.
      out.push({ queryKey: ['target', e.targetId ?? e.id] }, { queryKey: ['vps'] }, { queryKey: ['shortcuts'] });
      project(e.projectKey);
      break;
    case 'vps':
      out.push({ queryKey: ['vps'] });
      break;
    case 'project':
      // A rename changes the label every shortcut of that project shows.
      out.push({ queryKey: ['projects'] }, { queryKey: ['shortcuts'] });
      if (e.projectKey) project(e.projectKey);
      else out.push({ queryKey: ['project'] });
      break;
    case 'config':
      if (e.projectKey) out.push({ queryKey: ['project', e.projectKey, 'config'] }, { queryKey: ['project', e.projectKey, 'environments'] }, { queryKey: ['project', e.projectKey, 'targets'] });
      else out.push({ queryKey: ['project'] });
      out.push({ queryKey: ['target'] }, { queryKey: ['shared'] });
      break;
    case 'backup':
      out.push({ queryKey: e.targetId ? ['target', e.targetId, 'backups'] : ['target'] }, { queryKey: ['jobs'] });
      break;
    case 'migration':
      out.push({ queryKey: ['migrations'] }, { queryKey: ['migration', e.id] });
      project(e.projectKey);
      break;
    case 'notification':
      // The server only sends these to the person they are addressed to, so there is
      // nothing further to filter on here. One key covers the bell, the inbox and the
      // preference matrix, which all live under ['notifications'].
      return [{ queryKey: ['notifications'] }];
  }
  return out;
}

const LiveContext = createContext<LiveStatus>('connecting');
export const useLiveStatus = () => useContext(LiveContext);

/**
 * One EventSource for the whole app. Change events invalidate the matching queries (batched);
 * after a reconnect everything is refetched because events may have been missed.
 */
export function LiveEventsProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LiveStatus>('connecting');

  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      setStatus('offline');
      return;
    }
    let es: EventSource | null = null;
    let disposed = false;
    let attempts = 0;
    let missedEvents = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const pending = new Map<string, InvalidateQueryFilters>();

    const flush = () => {
      flushTimer = undefined;
      for (const f of pending.values()) void qc.invalidateQueries(f);
      pending.clear();
    };

    const connect = () => {
      if (disposed) return;
      es = new EventSource('/api/v1/events');
      es.onopen = () => {
        attempts = 0;
        setStatus('live');
        if (missedEvents) {
          missedEvents = false;
          void qc.invalidateQueries();
        }
      };
      es.addEventListener('change', (ev) => {
        try {
          const e = JSON.parse((ev as MessageEvent<string>).data) as ChangeEvent;
          for (const f of invalidationsFor(e)) pending.set(JSON.stringify(f), f);
          if (!flushTimer) flushTimer = setTimeout(flush, 250);
        } catch {
          /* ignore malformed events */
        }
      });
      es.onerror = () => {
        missedEvents = true;
        if (es && es.readyState === EventSource.CLOSED) {
          // The browser gave up (e.g. HTTP error): reconnect ourselves with backoff.
          es.close();
          attempts += 1;
          setStatus(attempts > 3 ? 'offline' : 'reconnecting');
          retryTimer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempts));
        } else {
          setStatus('reconnecting');
        }
      };
    };

    connect();
    return () => {
      disposed = true;
      es?.close();
      if (retryTimer) clearTimeout(retryTimer);
      if (flushTimer) clearTimeout(flushTimer);
    };
  }, [qc]);

  return <LiveContext.Provider value={status}>{children}</LiveContext.Provider>;
}
